/**
 * Pure planning half of the rendition backfill. Decides which artwork records
 * CAN get renditions (app-bucket originals this client may write next to) and
 * classifies everything else by reason so the runner can report, not silently
 * skip, the pieces it cannot touch — legacy scanner originals on another bucket
 * in particular.
 *
 * No DOM or Firebase imports; the async runner lives in
 * `hooks/newHooks/useRenditionBackfill.ts`.
 */
import { countBy, flatMap, sortBy, take } from "lodash"

import {
  type StudentArtwork,
  type StudentConstructor,
} from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"

import {
  getRenditionPath,
  parseStorageBucketFromUrl,
  parseStoragePathFromUrl,
  RENDITION_TIER,
} from "./artworkRenditionHelpers"

export const BACKFILL_SKIP_REASON = {
  /** Hidden pieces need no renditions regardless of other attributes. */
  HIDDEN: "hidden",
  /** Mobile records already carry `thumbnailUrl` and live in another bucket. */
  MOBILE: "mobile",
  /** A mobile crop is the display image; the original is not what we'd show. */
  CROPPED: "cropped",
  /** Legacy scanner originals on a bucket this client cannot write to. */
  FOREIGN_BUCKET: "foreignBucket",
  /** `url` is not a Firebase Storage download URL. */
  UNPARSABLE_URL: "unparsableUrl",
} as const

export type BackfillSkipReason =
  (typeof BACKFILL_SKIP_REASON)[keyof typeof BACKFILL_SKIP_REASON]

export interface BackfillCandidate {
  studentId: Uuid
  studentName: string
  artworkId: Uuid
  artwork: StudentArtwork
  /** Storage path of the ORIGINAL, derived from `artwork.url`. */
  objectPath: string
  thumbPath: string
  medPath: string
}

export interface BackfillSkipped {
  studentId: Uuid
  studentName: string
  artworkId: Uuid
  reason: BackfillSkipReason
  url: string
}

export interface BackfillPlan {
  candidates: BackfillCandidate[]
  skipped: BackfillSkipped[]
  skippedByReason: Partial<Record<BackfillSkipReason, number>>
}

const classifySkip = (
  artwork: StudentArtwork,
  appBucket: string
): BackfillSkipReason | null => {
  if (artwork.isHidden === true) return BACKFILL_SKIP_REASON.HIDDEN
  if (artwork.storageOrigin === "mobile") return BACKFILL_SKIP_REASON.MOBILE
  if (artwork.cropUrl) return BACKFILL_SKIP_REASON.CROPPED
  const bucket = parseStorageBucketFromUrl(artwork.url)
  if (bucket === null || parseStoragePathFromUrl(artwork.url) === null) {
    return BACKFILL_SKIP_REASON.UNPARSABLE_URL
  }
  if (bucket !== appBucket) return BACKFILL_SKIP_REASON.FOREIGN_BUCKET
  return null
}

/**
 * Walks every student's artwork map. Candidates are ordered by student then
 * `timeCreated` so a partial run leaves a comprehensible frontier.
 */
export const planRenditionBackfill = (
  students: readonly StudentConstructor[],
  appBucket: string | null | undefined
): BackfillPlan => {
  if (!appBucket) {
    throw new Error("Cannot plan a rendition backfill without an app bucket")
  }

  const candidates: BackfillCandidate[] = []
  const skipped: BackfillSkipped[] = []

  const entries = flatMap(students, (student) =>
    sortBy(Object.values(student.artwork ?? {}), "timeCreated").map(
      (artwork) => ({ student, artwork })
    )
  )

  for (const { student, artwork } of entries) {
    const reason = classifySkip(artwork, appBucket)
    if (reason) {
      skipped.push({
        studentId: student.id,
        studentName: student.name,
        artworkId: artwork.id,
        reason,
        url: artwork.url,
      })
      continue
    }
    // classifySkip guarantees this parses.
    const objectPath = parseStoragePathFromUrl(artwork.url) as string
    candidates.push({
      studentId: student.id,
      studentName: student.name,
      artworkId: artwork.id,
      artwork,
      objectPath,
      thumbPath: getRenditionPath(objectPath, RENDITION_TIER.THUMB),
      medPath: getRenditionPath(objectPath, RENDITION_TIER.MED),
    })
  }

  return {
    candidates,
    skipped,
    skippedByReason: countBy(skipped, "reason") as Partial<
      Record<BackfillSkipReason, number>
    >,
  }
}

/**
 * First `limit` candidates in plan order; the whole plan when `limit` is not a
 * positive number. DEBUG-ONLY: the only caller is the `/artwork-wall-debug`
 * panel's limit control, which exists so the e2e specs can prove the
 * write / idempotent-skip / abort mechanism on a handful of items instead of
 * re-encoding every fixture image on a shared CI runner. The PLAN is never
 * limited — the planner must still classify every record.
 */
export const limitBackfillCandidates = (
  candidates: readonly BackfillCandidate[],
  limit: number | null | undefined
): BackfillCandidate[] =>
  typeof limit === "number" && Number.isFinite(limit) && limit > 0
    ? take(candidates, Math.floor(limit))
    : [...candidates]

/**
 * Exactly one outcome per candidate, so `written + existing + failed` always
 * equals `processed`.
 */
export const BACKFILL_OUTCOME = {
  /**
   * At least one tier was written this run. A half-written pair that had only
   * its missing tier repaired counts here too — "written" means Storage
   * changed, not "both tiers were produced from scratch".
   */
  WRITTEN: "written",
  /** Both tiers already existed — the run is idempotent, nothing was touched. */
  EXISTING: "existing",
  FAILED: "failed",
} as const

export type BackfillOutcome =
  (typeof BACKFILL_OUTCOME)[keyof typeof BACKFILL_OUTCOME]

export interface BackfillResult {
  candidate: BackfillCandidate
  outcome: BackfillOutcome
  error?: string
}

export interface BackfillTally {
  total: number
  processed: number
  written: number
  existing: number
  failed: number
}

export const tallyBackfill = (
  results: readonly BackfillResult[],
  total: number
): BackfillTally => {
  const counts = countBy(results, "outcome")
  return {
    total,
    processed: results.length,
    written: counts[BACKFILL_OUTCOME.WRITTEN] ?? 0,
    existing: counts[BACKFILL_OUTCOME.EXISTING] ?? 0,
    failed: counts[BACKFILL_OUTCOME.FAILED] ?? 0,
  }
}
